import { listLibraryItems } from '@/backend/listLibraryItems'
import { useLibraryStore, type LibraryItem } from '@/modules/Library/libraryStore'
import { serializeLibraryItem } from '@/modules/Library/serializers'
import { assertIsNotNullOrUndefined } from '@/shared/utils/typeAssertions'
import { useLoadAllPaginatedResults } from '@/sharedComposables/useLoadAllPaginatedResults'
import type { Ref } from 'vue'

/**
 * Populates the library with all of its items.
 *
 * @param workspaceId - The ID of the workspace to populate the library for.
 * @param initialItems - The initial items to populate the library with. These items are
 * overwritten by the items returned from the backend. If not provided, the library will
 * be cleared before being populated.
 * @returns A function that can be called to populate the library.
 */
export const usePopulateLibrary = (workspaceId: Ref<string>, initialItems: LibraryItem[] = []) => {
  const libraryStore = useLibraryStore()
  const loadAllLibraryItems = useLoadAllPaginatedResults(
    ({ after, first }) => listLibraryItems({ workspaceId: workspaceId.value, after, first }),
    (entity) => {
      assertIsNotNullOrUndefined(libraryStore.library, 'Library ID is null')
      const fileSlug = libraryStore.library.fileProperty.slug
      const textSlug = libraryStore.library.textProperty.slug

      const fileField = entity.fields[fileSlug]
      assertIsNotNullOrUndefined(fileField, 'File field is not defined')
      const textField = entity.fields[textSlug]
      assertIsNotNullOrUndefined(textField, 'Text field is not defined')

      if (fileField.manual_value.value) {
        return serializeLibraryItem(entity, fileSlug)
      } else if (textField.manual_value.value) {
        return serializeLibraryItem(entity, textSlug)
      } else {
        return entity.id
      }
    },
    (idsOrItems) => {
      const { emptyIds, items } = idsOrItems.reduce<{
        items: LibraryItem[]
        emptyIds: string[]
      }>(
        (acc, idOrItem) => {
          if (typeof idOrItem === 'string') {
            acc.emptyIds.push(idOrItem)
          } else {
            const index = acc.items.findIndex((item) => item.id === idOrItem.id)
            if (index === -1) {
              acc.items.push(idOrItem)
            } else {
              acc.items[index] = idOrItem
            }
          }

          return acc
        },
        { items: [...initialItems], emptyIds: [] },
      )

      libraryStore.libraryItems = items
      libraryStore.emptyItemIds = emptyIds
    },
  )

  return loadAllLibraryItems
}
